import { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import {
    BrowserCacheLocation,
    InteractionType,
    IPublicClientApplication,
    LogLevel,
    PublicClientApplication,
} from '@azure/msal-browser';
import { LogInterface } from '@evorait/pace-logging';

const isIE = /MSIE |Trident\//.test(window.navigator.userAgent);

export function MSALInstanceFactory(
    clientId: string,
    authority: string,
    log: LogInterface,
    verbose = false,
): IPublicClientApplication {
    return new PublicClientApplication({
        auth: {
            clientId,
            authority,
            redirectUri: '/',
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
            storeAuthStateInCookie: isIE,
        },
        system: {
            loggerOptions: {
                logLevel: LogLevel.Verbose,
                loggerCallback: (level, message, containsPii): void => {
                    const logMessage = `MSAL: ${message}`;
                    if (containsPii) {
                        return;
                    }
                    switch (level) {
                        case LogLevel.Error:
                            log.error(logMessage);
                            return;
                        case LogLevel.Info:
                            log.info(logMessage);
                            return;
                        case LogLevel.Warning:
                            log.warn(logMessage);
                            return;
                        case LogLevel.Verbose:
                            if (verbose) log.debug(logMessage);
                            return;
                        case LogLevel.Trace:
                            if (verbose) log.debug(logMessage);
                            return;
                    }
                },
                piiLoggingEnabled: false,
            },
        },
    });
}

// MSAL Interceptor is required to request access tokens in order to access the protected resource (Graph)
export function MSALInterceptorConfigFactory(
    protectedRoutes: string[],
    fxAppScope: string,
    graphApiUrl: string,
): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, string[]>();
    protectedResourceMap.set(graphApiUrl, [fxAppScope]);
    protectedRoutes.forEach((protectedRoute) => protectedResourceMap.set(protectedRoute, [fxAppScope]));
    protectedResourceMap.set(window.location.origin, [fxAppScope]);
    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap,
    };
}

// MSAL Guard is required to protect routes and require authentication before accessing protected routes
export function MSALGuardConfigFactory(fxAppScope: string): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: [fxAppScope],
        },
    };
}
